// WebKit exposes one file item per image type on the pasteboard, so a single
// copied screenshot arrives as png + tiff + jpeg + bmp + ... all at once.
const TYPE_PREFERENCE = [
	"application/pdf",
	"image/png",
	"image/jpeg",
	"image/webp",
	"image/gif",
	"image/heic",
	"image/svg+xml",
]

const GENERIC_NAMES = new Set([
	"",
	"image",
	"file",
	"untitled",
	"unnamed",
	"clipboard",
	"pasted image",
	"pasted-image",
	"pasted_image",
])

function baseName(name: string): string {
	const normalized = name.trim().toLowerCase()
	const dot = normalized.lastIndexOf(".")
	return dot > 0 ? normalized.slice(0, dot) : normalized
}

function preferenceRank(type: string): number {
	const index = TYPE_PREFERENCE.indexOf(type.toLowerCase())
	return index === -1 ? TYPE_PREFERENCE.length : index
}

function pickBestRepresentation(files: File[]): File {
	return files.reduce((best, file) => {
		const rank = preferenceRank(file.type)
		const bestRank = preferenceRank(best.type)
		if (rank !== bestRank) {
			return rank < bestRank ? file : best
		}
		return file.size < best.size ? file : best
	})
}

/**
 * Collapse the alternative representations of a single pasted object down to
 * one file, while keeping genuinely distinct files pasted together.
 */
export function dedupeClipboardFiles(files: File[]): File[] {
	if (files.length < 2) {
		return files
	}

	const groups = new Map<string, File[]>()
	for (const file of files) {
		const base = baseName(file.name)
		const key = GENERIC_NAMES.has(base) ? " generic" : base
		const group = groups.get(key)
		if (group) {
			group.push(file)
		} else {
			groups.set(key, [file])
		}
	}

	return Array.from(groups.values()).map((group) =>
		group.length === 1 ? group[0]! : pickBestRepresentation(group),
	)
}

// Rich-text sources (Word, Google Docs, Slack) put an rtf or html flavor of the
// copied text on the clipboard as a file; attaching it swallows the paste.
const TEXT_FLAVORS = new Set([
	"text/rtf",
	"application/rtf",
	"text/richtext",
	"application/x-rtf",
	"text/html",
	"text/plain",
])

/** Files carried by a paste event, with duplicate representations collapsed. */
export function getClipboardFiles(
	items: DataTransferItemList | null | undefined,
): File[] {
	if (!items) {
		return []
	}

	const all = Array.from(items)
	const carriesText = all.some(
		(item) => item.kind === "string" && item.type.startsWith("text/"),
	)

	const files = all
		.filter((item) => item.kind === "file")
		.map((item) => item.getAsFile())
		.filter((file): file is File => file !== null)
		.filter(
			(file) => !(carriesText && TEXT_FLAVORS.has(file.type.toLowerCase())),
		)

	return dedupeClipboardFiles(files)
}

/**
 * True when a paste carries files and no text, so attaching them replaces the
 * default paste. With text present the text is the paste and must survive.
 */
export function pasteIsFilesOnly(files: File[], hasText: boolean): boolean {
	return files.length > 0 && !hasText
}

/**
 * Attaches a paste's files from a ProseMirror handlePaste and returns its result.
 * A files-only paste must return true: ProseMirror retries a declined paste with
 * no text 50ms later, which attached every file twice.
 */
export function attachPastedFiles(
	clipboardData: DataTransfer | null | undefined,
	attach: (files: File[]) => Promise<unknown>,
): boolean {
	const files = getClipboardFiles(clipboardData?.items)
	if (files.length === 0) {
		return false
	}
	void attach(files)
	return pasteIsFilesOnly(files, Boolean(clipboardData?.getData("text/plain")))
}
